import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import LoadingSpinner from '@/common/components/LoadingSpinner'
import Table from '@/common/components/Table'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import { internalBaseEquipments } from '@/common/constants/internalBaseEquipments'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { CompanyBranchData } from '@/common/types/companyBranches.type'
import { Option } from "@/common/types/option"
import { useEstablishmentCompanies } from '@/features/establishments/hooks/useEstablishmentCompanies'
import { EstablishmentCompanyTransformer } from '@/features/establishments/services/establishmentCompanyTransformer'
import {
  companyTotalizerFeesColumns,
  companyTotalizerProductsColumns,
} from '@/features/privateCompanies/constants/companyTotalizerDefaultColumns'
import { useAvailableCompanyBranches } from '@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches'
import useCompanyMovementsTotalizer from '@/features/privateCompanies/hooks/useCompanyMovementsTotalizer'
import ResumeList from '@/features/privateCompanies/pages/Totals/components/ResumeList'
import { CompanyMovementsTransformer } from '@/features/privateCompanies/services/movementsTransformer'
import { CompanyMovementsTotalizerData } from '@/features/privateCompanies/types/companyMovementsTotalizer.type'
import { useParams } from 'react-router-dom'

const Index = () => {
  const { id } = useParams()
  const { filterParams, setFilterParams } = useFilterParams({
    fromInternalBase: internalBaseEquipments.find(({ value }) => value === 'false')
  })

  const { date, formattedDate } = useFilterDate(filterParams)
  const [searchEstablishmentCompany, setSearchEstablishmentCompany] = useDebounce('')
  const [searchCompanyBranch, setSearchCompanyBranch] = useDebounce('')

  const { data: establishmentCompanyOptions } = useEstablishmentCompanies({
    filters: { search: searchEstablishmentCompany },
    mapper: Mapper.mapWithTransformer({ transformer: EstablishmentCompanyTransformer.optionTransformer() }),
  });

  const { data: companyBranchesOptions } = useAvailableCompanyBranches({
    companyId: id,
    filters: { search: searchCompanyBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({
      labelFieldName: 'display_name', valueFieldName: 'id',
    }),
  })

  const {
    data: movementsTotalizer,
    isPending: isPendingCompanyMovementsTotalizer,
    isFetching: isFetchingCompanyMovementsTotalizer,
    error: companyMovementsTotalizerError = {} as any,
    refetch: refetchCompanyMovementsTotalizer,
  } = useCompanyMovementsTotalizer({
    filters: {
      id: id || '',
      branchId: filterParams.branch?.value,
      establishmentBranchId: filterParams.establishmentCompany?.value,
      fromInternalBase: filterParams.fromInternalBase?.value,
      startDate: formattedDate.from,
      endDate: formattedDate.to,
    },
    mapper: Mapper.default<CompanyMovementsTotalizerData>(),
  })

  if (!id) return null

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={companyMovementsTotalizerError?.status}
      refetchFunction={refetchCompanyMovementsTotalizer}
    >
      <div className="flex flex-col gap-6 max-h-[calc(100vh-192px)] overflow-y-hidden pr-1 pt-1">
        <div className="flex items-center gap-4">
          <DatePickerWithRange
            date={date}
            setDate={(date) =>
              setFilterParams((params) => ({ ...params, date }))
            }
          />
          <ListFilters
            filters={[
              {
                className: 'w-44',
                onChange: (e) => setFilterParams((prev) => ({ ...prev, branch: e })),
                onInputChange: setSearchCompanyBranch,
                value: filterParams.branch,
                options: [{ label: 'Todas', value: '' }, ...(companyBranchesOptions || [])],
                placeholder: 'Filial',
              },
              {
                className: '',
                onChange: (establishmentCompany) => setFilterParams((prev) => ({ ...prev, establishmentCompany })),
                onInputChange: setSearchEstablishmentCompany,
                value: filterParams.establishmentCompany,
                options: [{ label: 'Todas', value: '' }, ...establishmentCompanyOptions as Option[]],
                placeholder: 'Estabelecimento'
              },
              {
                className: 'min-w-44',
                onChange: (e) =>
                  setFilterParams((prev) => ({
                    ...prev,
                    fromInternalBase: e,
                  })),
                value: filterParams.fromInternalBase,
                options: [{ label: 'Todos', value: '' }, ...(internalBaseEquipments || [])],
                placeholder: 'Exibir transações da base',
              }
            ]}
          />
        </div>
        <div className='flex justify-between'>
          <ResumeList data={movementsTotalizer} />
          <LoadingSpinner isLoading={isFetchingCompanyMovementsTotalizer} className='align-end' />
        </div>

        <div className="flex flex-col gap-3 flex-1 min-h-0">
          <div className={`flex flex-col flex-1 ${movementsTotalizer.product_movements?.length! < 3 ? 'min-h-[170px] max-h-[250px]' : 'min-h-[170px]'}`}>
            <p className="shrink-0">Produtos</p>
            <Table
              className={'flex-1'}
              columns={companyTotalizerProductsColumns}
              isLoadingData={isPendingCompanyMovementsTotalizer}
              page={1}
              rows={
                CompanyMovementsTransformer.productsTableTransformer()(
                  movementsTotalizer.product_movements || []
                ) || []
              }
              rowEmptyValue="--"
              totalPages={1}
              totalCount={movementsTotalizer.product_movements?.length || 0}
            />
          </div>

          <div className={`flex flex-col flex-1 ${movementsTotalizer.fees?.length! < 3 ? 'min-h-[170px] max-h-[250px]' : 'min-h-[170px]'}`}>
            <p className="shrink-0">Tarifas</p>
            <Table
              className={'flex-1'}
              columns={companyTotalizerFeesColumns}
              isLoadingData={isPendingCompanyMovementsTotalizer}
              page={1}
              rows={
                CompanyMovementsTransformer.feesTableTransformer()(
                  movementsTotalizer.fees || []
                ) || []
              }
              rowEmptyValue="--"
              totalPages={1}
              totalCount={movementsTotalizer.fees?.length || 0}
            />

          </div>
        </div>
      </div>


    </ErrorMessageWrapper >
  )
}

export default Index
